// API Client Configuration for Ruumi
// Based on Linda pattern with JWT token management and response interceptor

import axios, { type AxiosInstance, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import { useAuthenticationStore } from '@/stores/authenticationStore';

let apiClientInstance: AxiosInstance | null = null;

/**
 * Get or create the configured API client instance
 * Implements response interceptor pattern - returns data directly, not full response
 */
export async function useApiClient(): Promise<{ apiClient: AxiosInstance }> {
  if (apiClientInstance) {
    return { apiClient: apiClientInstance };
  }

  const baseURL = import.meta.env.VITE_API_BASE_URL || 'https://localhost:7222';

  apiClientInstance = axios.create({
    baseURL,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    }
  });

  // Request interceptor - inject JWT token
  apiClientInstance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const authStore = useAuthenticationStore();

      if (config.url?.includes('/api/Report/GetBookingCancellationsReport')) {
        console.log('[apiClient] request', {
          method: config.method,
          baseURL: config.baseURL,
          url: config.url,
          data: config.data,
          params: config.params
        });
      }
      
      if (authStore.tokens?.token) {
        config.headers.Authorization = `Bearer ${authStore.tokens.token}`;
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor - extract data automatically
  apiClientInstance.interceptors.response.use(
    (response: AxiosResponse) => {
      if (response.config.url?.includes('/api/Report/GetBookingCancellationsReport')) {
        console.log('[apiClient] response', {
          status: response.status,
          url: response.config.url,
          dataLength: Array.isArray(response.data) ? response.data.length : null
        });
      }
      // ⭐ CRITICAL: Returns data directly, not full response
      // This requires service methods to use type casting: as unknown as ResponseType
      return response.data;
    },
    async (error) => {
      const originalRequest = error.config;

      // Skip retry logic for RefreshToken endpoint to prevent infinite loop
      const isRefreshTokenRequest = originalRequest.url?.includes('/RefreshToken');

      // Handle 401 Unauthorized - attempt token refresh
      if (error.response?.status === 401 && !originalRequest._retry && !isRefreshTokenRequest) {
        originalRequest._retry = true;

        try {
          const authStore = useAuthenticationStore();
          const refreshSuccessful = await authStore.tryRefreshToken();

          if (refreshSuccessful) {
            // Retry original request with new token
            const newToken = authStore.tokens?.token;
            if (newToken) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return apiClientInstance!(originalRequest);
            }
          }
        } catch (refreshError) {
          // Refresh failed - redirect to login
          const authStore = useAuthenticationStore();
          await authStore.logout();
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }
      
      // If RefreshToken itself fails with 401, logout immediately
      if (error.response?.status === 401 && isRefreshTokenRequest) {
        const authStore = useAuthenticationStore();
        await authStore.logout();
        window.location.href = '/login';
        return Promise.reject(new Error('Session expired'));
      }

      // Handle other error responses
      if (error.response) {
        // Server responded with error status
        // Extract error message from various possible response structures
        let errorMessage = 'Server error';
        
        if (error.response.data) {
          if (typeof error.response.data === 'string') {
            errorMessage = error.response.data;
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message;
          } else if (error.response.data.title) {
            errorMessage = error.response.data.title;
          } else if (error.response.data.error) {
            errorMessage = error.response.data.error;
          }
        }
        
        console.error('API Error:', {
          status: error.response.status,
          message: errorMessage,
          url: originalRequest?.url
        });
        
        // Create enhanced error object with message property
        const enhancedError = new Error(errorMessage);
        (enhancedError as any).response = error.response;
        (enhancedError as any).status = error.response.status;
        
        return Promise.reject(enhancedError);
      } else if (error.request) {
        // Request made but no response received
        console.error('Network Error:', error.message);
        return Promise.reject(new Error('Network error - please check your connection'));
      } else {
        // Error in request setup
        console.error('Request Error:', error.message);
        return Promise.reject(error);
      }
    }
  );

  return { apiClient: apiClientInstance };
}

/**
 * Reset the API client instance (useful for logout)
 */
export function resetApiClient(): void {
  apiClientInstance = null;
}

/**
 * Create FormData from object (for file uploads)
 */
export function createFormData(data: Record<string, any>): FormData {
  const formData = new FormData();

  Object.entries(data).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      return; // Skip null/undefined values
    }

    if (value instanceof File) {
      formData.append(key, value);
    } else if (value instanceof Date) {
      formData.append(key, value.toISOString());
    } else if (typeof value === 'object' && !(value instanceof Blob)) {
      formData.append(key, JSON.stringify(value));
    } else {
      formData.append(key, String(value));
    }
  });

  return formData;
}
